"use client";

import { useTranslations } from "next-intl";
import { Link } from "@/navigation";
import Image from "next/image";

const CTA_BUTTON_CLASS =
	"inline-flex items-center hover:text-white justify-center rounded bg-themePrimary400 px-8 py-3 text-sm font-medium text-white transition-all duration-300 hover:bg-opacity-90 md:px-10 md:py-4 md:text-base";

const DEFAULT_CTA_BACKGROUND = "/image/new-version/machining/cta-background.jpg" as const;

export interface CTAProps {
	translationNamespace?: string;
	buttonHref?: string;
	images?: [string, string, string];
}

export default function CTA({
	translationNamespace = "fiveAxisCTA",
	buttonHref = "/upload",
	images,
}: CTAProps) {
	const t = useTranslations(translationNamespace);
	const isExternal = buttonHref.startsWith("http");
	const hasImages = images && images.length === 3;

	return (
		<section
			className={`relative w-full overflow-hidden py-8 sm:py-10 md:py-12 lg:py-16 ${
				hasImages ? "bg-[#e3e2e2]" : ""
			}`}
		>
			{!hasImages && (
				<div className="pointer-events-none absolute inset-0 z-0">
					<Image
						src={DEFAULT_CTA_BACKGROUND}
						alt=""
						fill
						className="object-cover object-center"
						sizes="100vw"
					/>
				</div>
			)}
			{hasImages && (
				<div className="absolute bottom-8 left-0 right-0 z-0 flex w-full items-end justify-center gap-8 px-4 sm:bottom-10 sm:gap-10 sm:px-6 md:bottom-12 md:gap-14 lg:bottom-14 lg:gap-16 xl:bottom-16 xl:gap-20">
					<div className="shrink-0 -translate-x-4 sm:-translate-x-5 md:-translate-x-6 lg:-translate-x-8">
						<div className="relative h-[112px] w-[112px] overflow-hidden sm:h-[136px] sm:w-[136px] md:h-[168px] md:w-[168px] lg:h-[196px] lg:w-[196px]">
							<Image
								src={images[0]}
								alt=""
								fill
								className="object-contain object-bottom"
								sizes="(max-width: 640px) 112px, (max-width: 768px) 136px, (max-width: 1024px) 168px, 196px"
							/>
						</div>
					</div>
					<div className="flex shrink-0 translate-x-14 items-end gap-8 sm:translate-x-16 sm:gap-10 md:translate-x-20 md:gap-14 lg:translate-x-24 lg:gap-16 xl:translate-x-28 xl:gap-20">
						<div className="relative h-[112px] w-[112px] translate-y-6 overflow-hidden sm:h-[136px] sm:w-[136px] sm:translate-y-8 md:h-[168px] md:w-[168px] md:translate-y-10 lg:h-[196px] lg:w-[196px] lg:translate-y-12">
							<Image
								src={images[1]}
								alt=""
								fill
								className="object-contain object-bottom"
								sizes="(max-width: 640px) 112px, (max-width: 768px) 136px, (max-width: 1024px) 168px, 196px"
							/>
						</div>
						<div className="relative h-[112px] w-[112px] overflow-hidden sm:h-[136px] sm:w-[136px] md:h-[168px] md:w-[168px] lg:h-[196px] lg:w-[196px]">
							<Image
								src={images[2]}
								alt=""
								fill
								className="object-contain object-bottom"
								sizes="(max-width: 640px) 112px, (max-width: 768px) 136px, (max-width: 1024px) 168px, 196px"
							/>
						</div>
					</div>
				</div>
			)}
			<div className="container relative z-10">
				<div className="flex flex-col items-center justify-center text-center">
					<div className="mb-8">
						<div className="flex flex-wrap items-end justify-center gap-x-3 gap-y-1 sm:gap-x-4">
							<span className="text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">
								{t("titleLine1")}
							</span>
							<span
								className="mb-[0.6em] h-[2px] w-12 shrink-0 bg-themePrimary400 sm:w-16 md:w-20 lg:w-24"
								aria-hidden
							/>
						</div>
						<h2 className="mt-2 text-2xl font-bold leading-tight text-black sm:mt-3 sm:text-[30px] md:text-4xl">
							{t("titleLine2")}
						</h2>
					</div>

					{isExternal ? (
						<a href={buttonHref} target="_blank" rel="noopener noreferrer" className={CTA_BUTTON_CLASS}>
							{t("buttonText")}
						</a>
					) : (
						<Link href={buttonHref} className={CTA_BUTTON_CLASS}>
							{t("buttonText")}
						</Link>
					)}
				</div>
			</div>
		</section>
	);
}
